/**
 * แหล่งข้อมูลของหน้า `/metadoc` และ `/DocumentControl` — สลับ "API เดิมของ Genie" ↔ "App Knowledge Center (ผ่าน proxy)"
 * แผนงาน: tasks/2026-10-27-metadoc-center-parity/TASK.md (D13, G2) · สัญญา: CONTRACT.md §6
 *
 * ── วิธีใช้ (ในหน้า Vue)
 *   this.kccSrc = createMetaDocSource({
 *     call: <ตัวเรียก API เดิมของหน้า (global property ที่หน้าใช้อยู่)>,
 *     isCenter: () => this.centerMode,          // อ่านใหม่ทุกครั้งที่เรียก ⇒ สลับโหมดหลัง mount ได้
 *     t: (k, p) => this.$t(k, p),
 *     swal: this.$swal,
 *   });
 *   แล้วแทน `<ตัวเรียกเดิม>.X(...)` ด้วย `this.kccSrc.X(...)` — ชื่อ/ลำดับ argument เท่าเดิมทุกตัว (ตาราง METHODS)
 *
 * ── โหมดปกติ (isCenter() = false) ⇒ `call[X](...args)` ตรง ๆ — พฤติกรรมเดิมทุกบรรทัด (popup/logout ของตัวเรียกเดิม)
 *
 * ── โหมด Center ⇒ axios instance ของไฟล์นี้เอง (`withCredentials`, ไม่มี interceptor) ยิง proxy ของ Genie
 *    `api/v1/KnowledgeCenterClient/metadoc/...` (ตาราง OPS) · proxy เป็นคนใส่ key/actor ของ Center — ไฟล์นี้ไม่ตั้ง
 *    header ของ Center เอง · callback ได้ซองเต็ม `{ status, message, data }` เหมือนตัวเรียกเดิม
 *    กติกา error (D13 — `validateStatus: () => true` แล้วตัดสินเอง):
 *      HTTP 2xx                                   ⇒ cb(body)
 *      HTTP 400/403/404/409 + body เป็นซอง        ⇒ cb(body)  (หน้าเดิมแสดง message ในกิ่ง status ≠ 0 เอง)
 *      424/412/502/5xx/401/อื่น ๆ หรือ body ไม่ใช่ซอง ⇒ swal.fire({icon:'error', title: t(kccErrorKey(e)), text: message})
 *                                                     แล้ว err(e)
 *      network / timeout (ไม่มี response)         ⇒ swal title t('kccCenterUnreachable') แล้ว err(e)
 *    **ไม่มี logout** — 401 ของ Genie (role ไม่ผ่าน APIAuthen) = popup ธรรมดา + err · proxy ไม่ส่ง 401 ของ Center ต่อ (เป็น 424)
 *    `e` = Error ที่มี `httpStatus`, `status` (APIStatus จากซอง), `data` (data ของซอง), `response` (`{status, data, ...}`
 *    ของ axios — หน้าเดิมอ่าน `err.response.data.message` ได้เหมือนเดิม), `cause` (error ดิบของ axios ถ้ามี)
 *    cb / err ที่ไม่ใช่ function ⇒ ข้าม (บางจุดของ DocumentControl ไม่ส่ง err)
 *
 * ── timeout (ฝั่ง browser): JSON 90 วินาที · FormData และ op ยาว (item-create/item-update/version-save/chunk-save)
 *    = 0 (ไม่จำกัด — server คุมด้วย UploadTimeoutSeconds)
 *
 * ── FormData: ทับ `Content-Type: multipart/form-data` ต่อคำขอ (ท่า kccApi.uploadVersion) ⇒ axios ไม่แปลงเป็น JSON และ
 *    browser ใส่ boundary เอง · object ⇒ JSON
 *
 * import แบบ relative + `import.meta.env?.` (มี `?.`) เพื่อให้ `tests/metaDocSource.test.mjs` import ไฟล์นี้ใต้ Node เปล่าได้
 * (Node ไม่รู้จัก alias `@/`) — Vite แทนค่าตอน build ตามเดิม
 */

import axios from 'axios';
import Swal from 'sweetalert2';
import { kccErrorKey } from './kccApi.js';

const BASE_URL = import.meta.env?.VITE_APP_API || 'http://localhost:5000/';

const ROOT = 'api/v1/KnowledgeCenterClient/metadoc';
const MD = `${ROOT}/masterdata`;

/** timeout ของคำขอ JSON ปกติ (ms) — op ยาว/FormData ใช้ 0 = ไม่จำกัด */
export const NORMAL_TIMEOUT_MS = 90000;

/** HTTP status ที่ body เป็นซอง ResponseModel แล้วส่งให้ callback ของหน้า (หน้าเดิมจัดการ status ≠ 0 เอง) */
const ENVELOPE_PASS_STATUSES = Object.freeze([400, 403, 404, 409]);

/**
 * ตาราง op → คำขอไปที่ proxy ของ Genie (CONTRACT §1–§4, §6)
 *   method · path (`{name}` = ค่าจาก argument ชื่อเดียวกันใน METHODS — ใส่ด้วย encodeURIComponent) ·
 *   kind: 'json' (body JSON) | 'multipart' (FormData ⇒ multipart; object ⇒ JSON เหมือนตัวเรียกเดิม) | 'none' (ไม่มี body) |
 *         'query' (ค่าอยู่ใน query string ของ path) · long: true ⇒ timeout 0
 */
export const OPS = Object.freeze({
  // ── กลุ่ม A — เอกสาร
  list: Object.freeze({ method: 'POST', path: `${ROOT}/list`, kind: 'json' }),
  'prefix-mapping': Object.freeze({ method: 'GET', path: `${ROOT}/prefix-mapping`, kind: 'none' }),
  'item-create': Object.freeze({ method: 'POST', path: `${ROOT}/item`, kind: 'multipart', long: true }),
  'item-update': Object.freeze({ method: 'PUT', path: `${ROOT}/item`, kind: 'multipart', long: true }),
  'item-delete': Object.freeze({ method: 'DELETE', path: `${ROOT}/item?vectorDBName={vectorDBName}`, kind: 'query' }),
  // ── กลุ่ม B — version / chunk
  versions: Object.freeze({ method: 'POST', path: `${ROOT}/versions`, kind: 'json' }),
  'version-save': Object.freeze({ method: 'POST', path: `${ROOT}/version`, kind: 'multipart', long: true }),
  'version-update': Object.freeze({ method: 'PUT', path: `${ROOT}/version`, kind: 'json' }),
  chunks: Object.freeze({ method: 'POST', path: `${ROOT}/chunks`, kind: 'json' }),
  'chunk-save': Object.freeze({ method: 'POST', path: `${ROOT}/chunk`, kind: 'json', long: true }),
  // ── กลุ่ม C — ดอกเบี้ย
  'interest-list': Object.freeze({ method: 'POST', path: `${ROOT}/interest-rates`, kind: 'json' }),
  'interest-add': Object.freeze({ method: 'POST', path: `${ROOT}/interest-rate`, kind: 'json' }),
  'interest-update': Object.freeze({ method: 'PUT', path: `${ROOT}/interest-rate`, kind: 'json' }),
  // ── กลุ่ม D — master data ของ Center
  'interest-master': Object.freeze({ method: 'GET', path: `${MD}/interest-master`, kind: 'none' }),
  'mdcat-list': Object.freeze({ method: 'GET', path: `${MD}/metadoc-categories`, kind: 'none' }),
  'mdcat-create': Object.freeze({ method: 'POST', path: `${MD}/metadoc-categories`, kind: 'json' }),
  'mdcat-update': Object.freeze({ method: 'PUT', path: `${MD}/metadoc-categories/{id}`, kind: 'json' }),
  'mdcat-delete': Object.freeze({ method: 'DELETE', path: `${MD}/metadoc-categories/{id}`, kind: 'none' }),
  'mdsub-list': Object.freeze({ method: 'GET', path: `${MD}/metadoc-subcategories/category/{categoryTitle}`, kind: 'none' }),
  'mdsub-create': Object.freeze({ method: 'POST', path: `${MD}/metadoc-subcategories`, kind: 'json' }),
  'mdsub-update': Object.freeze({ method: 'PUT', path: `${MD}/metadoc-subcategories/{id}`, kind: 'json' }),
  'mdsub-delete': Object.freeze({ method: 'DELETE', path: `${MD}/metadoc-subcategories/{id}`, kind: 'none' }),
  'mdpst-list': Object.freeze({ method: 'GET', path: `${MD}/metadoc-product-subtypes/category/{productCategory}`, kind: 'none' }),
  'mdpst-create': Object.freeze({ method: 'POST', path: `${MD}/metadoc-product-subtypes`, kind: 'json' }),
  'mdpst-update': Object.freeze({ method: 'PUT', path: `${MD}/metadoc-product-subtypes/{id}`, kind: 'json' }),
  'mdpst-delete': Object.freeze({ method: 'DELETE', path: `${MD}/metadoc-product-subtypes/{id}`, kind: 'none' }),
  'macat-list': Object.freeze({ method: 'GET', path: `${MD}/meta-account-categories`, kind: 'none' }),
  'macat-create': Object.freeze({ method: 'POST', path: `${MD}/meta-account-categories`, kind: 'json' }),
  'macat-update': Object.freeze({ method: 'PUT', path: `${MD}/meta-account-categories/{id}`, kind: 'json' }),
  'macat-delete': Object.freeze({ method: 'DELETE', path: `${MD}/meta-account-categories/{id}`, kind: 'none' }),
  'masub-list': Object.freeze({ method: 'GET', path: `${MD}/meta-account-subcategories/category/{categoryId}`, kind: 'none' }),
  'masub-create': Object.freeze({ method: 'POST', path: `${MD}/meta-account-subcategories`, kind: 'json' }),
  'masub-update': Object.freeze({ method: 'PUT', path: `${MD}/meta-account-subcategories/{id}`, kind: 'json' }),
  'masub-delete': Object.freeze({ method: 'DELETE', path: `${MD}/meta-account-subcategories/{id}`, kind: 'none' }),
});

/** ชื่อ argument ที่เป็น body (ที่เหลือคือค่าที่ใส่ลง `{name}` ของ path) */
const BODY_PARAMS = Object.freeze(['request', 'formData']);

/**
 * เมธอดที่หน้าใช้ → op + argument นำหน้า (ก่อน `callback, error`) ตามลำดับเดียวกับตัวเรียกเดิม (call.js)
 * ชื่อ argument: `request`/`formData` = body · ชื่ออื่น = ค่าที่ใส่ลง `{ชื่อ}` ใน path ของ op
 */
export const METHODS = Object.freeze({
  // ── MetaDoc.vue
  GetMetaDocList: Object.freeze({ op: 'list', params: Object.freeze(['request']) }),
  GetMetaDocPrefixMapping: Object.freeze({ op: 'prefix-mapping', params: Object.freeze([]) }),
  CreateMetaDocItem: Object.freeze({ op: 'item-create', params: Object.freeze(['request']) }),
  UpdateMetaDocItem: Object.freeze({ op: 'item-update', params: Object.freeze(['request']) }),
  DeleteVectorDBNameByName: Object.freeze({ op: 'item-delete', params: Object.freeze(['vectorDBName']) }),
  GetMetaDocCategories: Object.freeze({ op: 'mdcat-list', params: Object.freeze([]) }),
  CreateMetaDocCategory: Object.freeze({ op: 'mdcat-create', params: Object.freeze(['request']) }),
  UpdateMetaDocCategory: Object.freeze({ op: 'mdcat-update', params: Object.freeze(['id', 'request']) }),
  DeleteMetaDocCategory: Object.freeze({ op: 'mdcat-delete', params: Object.freeze(['id']) }),
  GetMetaDocSubCategoriesByCategory: Object.freeze({ op: 'mdsub-list', params: Object.freeze(['categoryTitle']) }),
  CreateMetaDocSubCategory: Object.freeze({ op: 'mdsub-create', params: Object.freeze(['request']) }),
  UpdateMetaDocSubCategory: Object.freeze({ op: 'mdsub-update', params: Object.freeze(['id', 'request']) }),
  DeleteMetaDocSubCategory: Object.freeze({ op: 'mdsub-delete', params: Object.freeze(['id']) }),
  GetMetaDocProductSubtypesByCategory: Object.freeze({ op: 'mdpst-list', params: Object.freeze(['productCategory']) }),
  CreateMetaDocProductSubtype: Object.freeze({ op: 'mdpst-create', params: Object.freeze(['request']) }),
  UpdateMetaDocProductSubtype: Object.freeze({ op: 'mdpst-update', params: Object.freeze(['id', 'request']) }),
  DeleteMetaDocProductSubtype: Object.freeze({ op: 'mdpst-delete', params: Object.freeze(['id']) }),
  // ── DocumentControl.vue
  GetDocumentVersions: Object.freeze({ op: 'versions', params: Object.freeze(['request']) }),
  SaveDocumentVersionForm: Object.freeze({ op: 'version-save', params: Object.freeze(['formData']) }),
  UpdateDocumentVersion: Object.freeze({ op: 'version-update', params: Object.freeze(['request']) }),
  GetDocumentChunks: Object.freeze({ op: 'chunks', params: Object.freeze(['request']) }),
  SaveEditDocumentChunk: Object.freeze({ op: 'chunk-save', params: Object.freeze(['request']) }),
  GetMetaDocInterestRates: Object.freeze({ op: 'interest-list', params: Object.freeze(['request']) }),
  AddMetaDocInterestRate: Object.freeze({ op: 'interest-add', params: Object.freeze(['request']) }),
  UpdateMetaDocInterestRate: Object.freeze({ op: 'interest-update', params: Object.freeze(['request']) }),
  GetInterestRateCategories: Object.freeze({ op: 'interest-master', params: Object.freeze([]) }),
  GetMetaAccountCategories: Object.freeze({ op: 'macat-list', params: Object.freeze([]) }),
  CreateMetaAccountCategory: Object.freeze({ op: 'macat-create', params: Object.freeze(['request']) }),
  UpdateMetaAccountCategory: Object.freeze({ op: 'macat-update', params: Object.freeze(['id', 'request']) }),
  DeleteMetaAccountCategory: Object.freeze({ op: 'macat-delete', params: Object.freeze(['id']) }),
  GetMetaSubCategoriesByCategory: Object.freeze({ op: 'masub-list', params: Object.freeze(['categoryId']) }),
  CreateMetaAccountSubCategory: Object.freeze({ op: 'masub-create', params: Object.freeze(['request']) }),
  UpdateMetaAccountSubCategory: Object.freeze({ op: 'masub-update', params: Object.freeze(['id', 'request']) }),
  DeleteMetaAccountSubCategory: Object.freeze({ op: 'masub-delete', params: Object.freeze(['id']) }),
});

/** axios instance ของโหมด Center (ไม่ใช้ instance/interceptor ของตัวเรียกเดิม) · export ไว้ให้เทสต์ใส่ adapter ปลอม */
export const metaDocHttp = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

const isFormData = (value) => typeof FormData !== 'undefined' && value instanceof FormData;

const isEnvelope = (body) => !!body && typeof body === 'object' && typeof body.status === 'number';

/** แทน `{name}` ใน path ด้วยค่าที่ encode แล้ว (ค่า null/undefined เขียนเป็นสตริงตรง ๆ เหมือน template literal เดิม) */
function buildUrl(path, values) {
  return path.replace(/\{([A-Za-z0-9_]+)\}/g, (_, key) => encodeURIComponent(String(values[key])));
}

/**
 * ประกอบ config ของ axios สำหรับ op หนึ่ง
 * @param {{method: string, path: string, kind: string, long?: boolean}} op
 * @param {Record<string, *>} values ค่าของ argument นำหน้า (ตามชื่อใน METHODS)
 * @param {*} body
 * @param {boolean} hasBody
 */
export function buildRequestConfig(op, values, body, hasBody) {
  const config = {
    method: op.method,
    url: buildUrl(op.path, values),
    withCredentials: true,
    validateStatus: () => true,
    headers: { Accept: 'application/json' },
    timeout: op.long ? 0 : NORMAL_TIMEOUT_MS,
  };
  if (!hasBody) return config;
  if (isFormData(body)) {
    // ⚠️ ต้องทับเป็น multipart — ถ้าเป็น JSON axios จะ stringify FormData ทิ้งไฟล์เงียบ ๆ
    config.headers['Content-Type'] = 'multipart/form-data';
    config.timeout = 0;
    config.data = body;
  } else {
    config.headers['Content-Type'] = 'application/json';
    config.data = body === undefined ? {} : body;
  }
  return config;
}

/** Error ที่ส่งให้ `err` ของหน้า — `kccErrorKey` อ่าน `httpStatus` · หน้าเดิมอ่าน `response.data.message` ได้ */
function toSourceError(response, cause) {
  const body = response ? response.data : undefined;
  const envelope = body && typeof body === 'object' ? body : null;
  const message =
    (envelope && envelope.message) ||
    (cause && cause.message) ||
    (response ? `Request failed with status code ${response.status}` : 'Network Error');
  const e = new Error(message);
  e.httpStatus = response ? response.status : undefined;
  e.status = envelope ? envelope.status : undefined;
  e.data = envelope ? envelope.data : undefined;
  e.response = response;
  e.cause = cause;
  e.isMetaDocSource = true;
  return e;
}

/**
 * @param {Object} options
 * @param {Object} options.call ตัวเรียก API เดิมของหน้า — โหมดปกติเรียก `call[X](...args)` (this = call)
 * @param {(() => boolean)|boolean} options.isCenter อ่านทุกครั้งที่เรียกเมธอด
 * @param {(key: string, params?: *) => string} [options.t] i18n (ค่าตั้งต้นคืน key)
 * @param {{fire: Function}} [options.swal] ค่าตั้งต้น sweetalert2
 * @param {{request: (config: Object) => Promise<{status: number, data: *}>}} [options.http] ค่าตั้งต้น metaDocHttp
 * @returns {Record<keyof typeof METHODS, Function>}
 */
export function createMetaDocSource({ call, isCenter, t, swal, http } = {}) {
  const translate = typeof t === 'function' ? t : (key) => key;
  const popup = swal || Swal;
  const client = http || metaDocHttp;
  const centerOn = () => (typeof isCenter === 'function' ? !!isCenter() : !!isCenter);

  function fail(e, errorCb) {
    const key = e.httpStatus ? kccErrorKey(e) : 'kccCenterUnreachable';
    const text = (e.response && e.response.data && typeof e.response.data === 'object' && e.response.data.message) || '';
    try {
      popup.fire({ icon: 'error', title: translate(key), text, showConfirmButton: true });
    } catch (popupError) {
      console.error('metaDocSource: popup failed', popupError);
    }
    if (typeof errorCb === 'function') errorCb(e);
  }

  async function centerCall(opName, values, body, hasBody, callback, errorCb) {
    const op = OPS[opName];
    const config = buildRequestConfig(op, values, body, hasBody);
    let response;
    try {
      response = await client.request(config);
    } catch (cause) {
      // validateStatus = true ⇒ เข้ามาที่นี่เฉพาะ network/timeout/ยกเลิก — แต่ถ้า http ปลอม/อื่น reject พร้อม response ก็รับไว้
      if (cause && cause.response) {
        response = cause.response;
      } else {
        fail(toSourceError(undefined, cause), errorCb);
        return;
      }
    }
    const status = response ? response.status : 0;
    const data = response ? response.data : undefined;
    if (status >= 200 && status < 300) {
      if (typeof callback === 'function') callback(data);
      return;
    }
    if (ENVELOPE_PASS_STATUSES.includes(status) && isEnvelope(data)) {
      if (typeof callback === 'function') callback(data);
      return;
    }
    fail(toSourceError(response), errorCb);
  }

  const source = {};
  Object.keys(METHODS).forEach((name) => {
    const { op, params } = METHODS[name];
    source[name] = function metaDocSourceMethod(...args) {
      if (!centerOn()) return call[name](...args);
      const values = {};
      let body;
      let hasBody = false;
      params.forEach((param, index) => {
        if (BODY_PARAMS.includes(param)) {
          body = args[index];
          hasBody = true;
        } else {
          values[param] = args[index];
        }
      });
      return centerCall(op, values, body, hasBody, args[params.length], args[params.length + 1]);
    };
  });
  return Object.freeze(source);
}

export default createMetaDocSource;
